<template>
  <div class="col-12 mb-3">
    <div class="zone-card card-item">
      <div class="zone-card-content">
        <!-- 헤더: 영역 이름 + 정보 -->
        <div class="zone-header-row" @click.stop="onExpandToggle">
          <div class="zone-title-wrapper" v-touch:hold="onTouchHold">
            <i class="fa-regular fa-house zone-icon"></i>
            <span class="zone-title">{{ props.data.name }}</span>
          </div>
          <div class="zone-header-info">
            <!-- 등록된 방 수 -->
            <div class="zone-info-badge">
              <i class="fa-brands fa-microsoft me-1 text-primary"></i>
              <span>{{ props.data.groups.length }}개</span>
            </div>
          </div>
          <div class="zone-expand-toggle">
            <i :class="`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`" style="font-size: 0.8rem;"></i>
          </div>
        </div>

        <!-- 본문: 전체 확장/축소 가능 -->
        <transition name="expand">
          <div v-if="isExpanded" class="zone-card-expandable-content">
            <!-- 본문 -->
            <div class="zone-card-body">
              <!-- 가상스위치 제어기 미등록 경고 -->
              <div v-if="!hasController" class="zone-warning">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>
                <span>가상스위치 제어기 미등록시에는 네트워크 설정을 할 수 없습니다. 가상스위치 제어기 추가 버튼을 눌러 추가 후 설정을 진행하십시요.</span>
              </div>
            </div>

            <!-- 가상스위치 제어기 목록 -->
            <div class="zone-controllers-section">
              <div class="zone-controllers-header">
                <i class="fa-solid fa-microchip me-2 text-primary"></i>
                <span class="zone-controllers-title">가상스위치 제어기 목록</span>
                <span class="zone-controllers-count">({{ props.data.controllers.length }}개)</span>
              </div>
              <div class="zone-controllers-list">
                <div v-if="hasController" class="zone-controllers-items">
                  <div
                    v-for="controller in props.data.controllers"
                    :key="controller._id"
                    class="zone-controller-item"
                  >
                    <div class="controller-item-main">
                      <div class="controller-item-info">
                        <div class="controller-item-name">
                          <i class="fa-solid fa-microchip me-2 text-primary"></i>
                          <span>{{ getControllerName(controller) }}</span>
                        </div>
                        <div class="controller-item-details">
                          <span class="controller-item-serial">
                            <i class="fa-solid fa-hashtag me-1"></i>
                            {{ controller._id }}
                          </span>
                          <span v-if="hasValidVersion(controller)" class="controller-item-version">
                            <i class="fa-solid fa-code-branch me-1"></i>
                            v{{ controller.version }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="zone-controllers-empty">
                  <i class="fa-solid fa-info-circle me-2"></i>
                  <span>등록된 가상스위치 제어기가 없습니다.</span>
                </div>
              </div>
            </div>

            <!-- 하단: 추가/설정 버튼 -->
            <div class="zone-card-footer">
              <div class="row g-2">
                <div class="col-6">
                  <button
                    type="button"
                    class="btn btn-primary w-100"
                    @click.stop="onRegistControllerClick"
                  >
                    추가
                  </button>
                </div>
                <div class="col-6">
                  <button
                    type="button"
                    class="btn btn-outline-primary w-100"
                    @click.stop="onSettingControllerClick"
                    :disabled="!hasController"
                  >
                    설정
                  </button>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <ZoneModal
      v-if="zoneModal.visible"
      :visible="zoneModal.visible"
      :okCallback="onUpdateZoneName"
      :cancelCallback="zoneModal.hide"
      :zoneName="props.data?.name"
    />
  </div>
</template>

<script setup>
import { onMounted, defineProps, computed, getCurrentInstance ,reactive, ref} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Axios, { ZoneApi } from '@/api';
import useMqttControl from '@/common/hooks/useMqttControl';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import ZoneModal from '@/views/rooms/components/ZoneModal.vue';
import useModal from '@/common/hooks/useModal';
import { resolveControllerDisplayName } from '@/common/utils/controllerDisplayName';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const { emit } = getCurrentInstance();
const router = useRouter();
const props = defineProps(['index', 'data', 'onZoneUpdated']);
const store = useStore();
const zoneModal = reactive(useModal());
const isExpanded = ref(false); // 기본적으로 접힌 상태

const backColor = computed(() => {
  if (props.index % 5 === 0) {
    return 'bg-gradient-custom-orange';
  } else if (props.index % 5 === 1) {
    return 'bg-gradient-custom-pink';
  } else if (props.index % 5 === 2) {
    return 'bg-gradient-custom-teal';
  } else if (props.index % 5 === 3) {
    return 'bg-gradient-custom-indigo';
  } else {
    return 'bg-gradient-custom-blue';
  }
});

onMounted(() => {
  console.log('VirtualZoneItem.onMounted().props =>', props.data.controllers);
  
  // 현재 선택된 zone인 경우 카드를 기본적으로 펼침
  const currentZone = store.getters['home/getCurrentZone'];
  if (currentZone && currentZone._id === props.data._id) {
    isExpanded.value = true;
  }
});

const hasController = computed(() =>{
  return props.data.controllers.length > 0
});

const onExpandToggle = () => {
  isExpanded.value = !isExpanded.value;
};

const getControllerName = (controller) => {
  const products = store.getters['home/getProducts'] || [];
  return resolveControllerDisplayName(controller, products, '가상스위치 제어기');
};

const hasValidVersion = (controller) => {
  const version = controller?.version;
  return version !== undefined && version !== null && version !== '' && Number(version) > 0;
};

const onRegistControllerClick = () => {
  router.push(`/setting/virtual/regist/${props.data._id}`);
}

const onSettingControllerClick = () =>{
  if (!hasController.value) {
    emitter.emit('showPopAlert', {
      description: '등록된 가상스위치 제어기가 없습니다.\n먼저 가상스위치 제어기를 등록해주세요.',
    });
    return;
  }
  router.push(`/setting/virtual/update/${props.data._id}`);
}

const onDeleteClick = () => {
  console.log('VirtualZoneItem.onDeleteClick =>', {
    name: props.data.name,
    zoneId: props.data._id,
  });

  emitter.emit('showPopConfirm', {
    title: '영역 삭제',
    description: `${props.data.name} 영역을 삭제하시겠습니까?`,
    okText: "삭제",
    cancelText: '취소',
    okCallback: () => setTimeout(() => emit('onDeleteClick', { name: props.data.name, zoneId: props.data._id }), 100)
  });
};

const onTouchHold = () => {
  zoneModal.show();
}

const onUpdateZoneName = (value) => {
  requestUpdateZone({
    name: value,
  });
  zoneModal.hide();
};

const requestUpdateZone = async (params) => {
  try {
    const result = await Axios.patch(
      ZoneApi.UPDATE_ZONE.replace(':id', props.data._id),
      params,
    );
    if (result.resultCode === '0000') {
      store.commit('home/updateZone', {
        id: props.data._id,
        data: params,
      });
      emit('onZoneUpdated');
    } else {
      emitter.emit('showPopAlert', {
        description: result.resultMessage,
      });
    }
  } catch (error) {
    emitter.emit('showPopAlert', {
      description: '영역 정보 수정에 실패했습니다.',
    });
  }
};
</script>

<style scoped>
.zone-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  max-width: 500px;
  margin: 0 auto;
}

.zone-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.zone-card-content {
  padding: 1rem;
}

.zone-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
}

.zone-expand-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  transition: all 0.2s ease;
}

.zone-expand-toggle:hover {
  color: #212529;
}

.zone-title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.zone-icon {
  color: #0d6efd;
  font-size: 1.1rem;
}

.zone-title {
  font-size: 1rem;
  font-weight: 700;
  color: #212529;
}

.zone-header-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.zone-info-badge {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  color: #495057;
  font-size: 0.85rem;
}

.zone-card-expandable-content {
  margin-top: -0.1rem;
}

.zone-card-body {
  padding: 0 0 0.75rem 0;
}

.zone-warning {
  padding: 0.7rem 0.8rem;
  border-radius: 0.75rem;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
}

.zone-controllers-section {
  margin-bottom: 0.9rem;
}

.zone-controllers-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.zone-controllers-title {
  font-weight: 800;
  color: #212529;
}

.zone-controllers-count {
  color: #6c757d;
  font-size: 0.85rem;
}

.zone-controllers-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.zone-controller-item {
  padding: 0.6rem 0.8rem;
  border-radius: 0.9rem;
  border: 1px solid #e9ecef;
  background-color: #ffffff;
  transition: all 0.2s ease;
}

.zone-controller-item:hover {
  border-color: #0d6efd;
}

.controller-item-main {
  display: flex;
  flex-direction: column;
}

.controller-item-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.controller-item-name {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 700;
  color: #212529;
}

.controller-item-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  color: #6c757d;
}

.controller-item-serial {
  font-size: 0.85rem;
}

.controller-item-version {
  font-size: 0.85rem;
  color: #0d6efd;
}

.zone-controllers-empty {
  padding: 0.8rem;
  border-radius: 0.9rem;
  background-color: #f8f9fa;
  border: 1px dashed #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #6c757d;
}

.zone-card-footer {
  border-top: 1px solid #e9ecef;
  padding-top: 0.7rem;
}
</style>

